import React, { useState } from "react";
import { useSelector } from "react-redux";
import { setDocData } from "../../../utils/firebase/Firebase.utils";
import IOSSpinner from "../../../components/IOSSpinner";
import CompanyTerms from "../../../components/CompanyTerms";
import TaskAssignmentModule from "../../../components/TaskAssignmentModule";
import CommentsSection from "../../../components/CommentsSection";
import HistoryModule from "../../../components/HistoryModule";
import SelectInput from "../Components/SelectInput";
import PhotoCarousel from "../../../pages/User/Marketplace/PhotoCarousel";
import {
  FiTool,
  FiClock,
  FiCheckCircle,
  FiAlertTriangle,
  FiCalendar,
  FiEdit2,
  FiX,
  FiAlertCircle,
} from "react-icons/fi";
import toast from "react-hot-toast";

export default function ServiceRequestDetails({
  request,
  onClose,
  setSelectedRequest,
}) {
  const currentUser = useSelector((state) => state.userObject);
  const [editMode, setEditMode] = useState(false);
  const [comments, setComments] = useState(request?.comments || []);
  const [savingComment, setSavingComment] = useState(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [assignedToArray, setAssignedToArray] = useState(
    request?.assignedTo || []
  );
  const [resolutionText, setResolutionText] = useState(
    request?.resolution || ""
  );
  const [resolutionSaving, setResolutionSaving] = useState(false);
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [newStatus, setNewStatus] = useState(request?.status || "");
  const [updateHappened, setUpdateHappened] = useState(false);
  const [estimatedCost, setEstimatedCost] = useState(
    request?.estimatedCost || ""
  );
  const [costSaving, setCostSaving] = useState(false);

  if (!request) return null;

  const allowedToEdit = [
    "admin",
    "super-admin",
    "property-manager",
    "security",
  ];
  const canEdit = allowedToEdit.some((role) =>
    currentUser.roles?.includes(role)
  );

  const statusOptions = [
    { label: "Pending", value: "Pending" },
    { label: "Scheduled", value: "Scheduled" },
    { label: "In Progress", value: "In Progress" },
    { label: "Completed", value: "Completed" },
    { label: "Cancelled", value: "Cancelled" },
  ];

  const handleSelectStatus = (selected) => {
    setNewStatus(selected.value);
    setUpdateHappened(true);
  };

  const handleUpdateStatus = async () => {
    setStatusUpdateLoading(true);
    try {
      if (newStatus === request.status) {
        setStatusUpdateLoading(false);
        return;
      }

      if (newStatus === "Completed" && !resolutionText) {
        toast.error("Please add a resolution before marking as completed");
        setStatusUpdateLoading(false);
        return;
      }

      const updatedRequest = {
        ...request,
        status: newStatus,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid,
        updatedName: currentUser.displayName,
        history: [
          ...(request?.history || []),
          {
            action: "Status changed to",
            at: new Date().toLocaleString(),
            by: currentUser.displayName,
            to: newStatus,
          },
        ],
      };

      await setDocData("serviceRequests", request.id, updatedRequest);
      setSelectedRequest(updatedRequest);
      toast.success(`Request status updated to ${newStatus}`);
      setUpdateHappened(false);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update request status");
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const handleCommentSubmit = async (commentText) => {
    setSavingComment(true);
    try {
      const commentObject = {
        message: commentText,
        createdBy: currentUser.displayName,
        createdAt: new Date().toLocaleString(),
        email: currentUser.email,
        id: Date.now().toString(),
      };

      const updatedComments = [...(request.comments || []), commentObject];

      const updatedRequest = {
        ...request,
        comments: updatedComments,
        updatedAt: new Date().toISOString(),
        history: [
          ...(request?.history || []),
          {
            action: "Comment added",
            at: new Date().toLocaleString(),
            by: currentUser.displayName,
            to:
              commentText.substring(0, 30) +
              (commentText.length > 30 ? "..." : ""),
          },
        ],
      };

      await setDocData("serviceRequests", request.id, updatedRequest);
      setSelectedRequest(updatedRequest);
      setComments(updatedComments);
      toast.success("Comment added successfully");
      return true;
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
      return false;
    } finally {
      setSavingComment(false);
    }
  };

  const handleCommentDelete = async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?"))
      return;

    try {
      const updatedComments = (request.comments || []).filter(
        (comment) => comment.id !== commentId
      );

      const updatedRequest = {
        ...request,
        comments: updatedComments,
        updatedAt: new Date().toISOString(),
        history: [
          ...(request?.history || []),
          {
            action: "Comment deleted",
            at: new Date().toLocaleString(),
            by: currentUser.displayName,
            to: "",
          },
        ],
      };

      await setDocData("serviceRequests", request.id, updatedRequest);
      setSelectedRequest(updatedRequest);
      setComments(updatedComments);
      toast.success("Comment deleted successfully");
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Failed to delete comment");
    }
  };

  const handleResolutionChange = (e) => {
    setResolutionText(e.target.value);
  };

  const handleResolutionUpdate = async () => {
    setResolutionSaving(true);
    try {
      const updatedRequest = {
        ...request,
        resolution: resolutionText,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid,
        updatedName: currentUser.displayName,
        history: [
          ...(request?.history || []),
          {
            action: "Resolution updated",
            at: new Date().toLocaleString(),
            by: currentUser.displayName,
            to:
              resolutionText.substring(0, 30) +
              (resolutionText.length > 30 ? "..." : ""),
          },
        ],
      };

      await setDocData("serviceRequests", request.id, updatedRequest);
      setSelectedRequest(updatedRequest);
      toast.success("Resolution updated successfully");
    } catch (error) {
      console.error("Error updating resolution:", error);
      toast.error("Failed to update resolution");
    } finally {
      setResolutionSaving(false);
    }
  };

  const handleAssignmentUpdate = async () => {
    setAssignmentSaving(true);
    try {
      const assigneeNames = assignedToArray.map((a) => a.label).join(", ");

      const updatedRequest = {
        ...request,
        assignedTo: assignedToArray,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid,
        updatedName: currentUser.displayName,
        history: [
          ...(request?.history || []),
          {
            action: "Service request assigned to",
            at: new Date().toLocaleString(),
            by: currentUser.displayName,
            to: assigneeNames || "No one",
          },
        ],
      };

      await setDocData("serviceRequests", request.id, updatedRequest);
      setSelectedRequest(updatedRequest);
      toast.success("Assignment updated successfully");
    } catch (error) {
      console.error("Error updating assignment:", error);
      toast.error("Failed to update assignment");
    } finally {
      setAssignmentSaving(false);
    }
  };

  const handleCostChange = (e) => {
    // Allow only numbers and decimal point
    const value = e.target.value;
    if (value === "" || /^\d+(\.\d{0,2})?$/.test(value)) {
      setEstimatedCost(value);
    }
  };

  const handleCostUpdate = async () => {
    setCostSaving(true);
    try {
      const updatedRequest = {
        ...request,
        estimatedCost: estimatedCost,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid,
        updatedName: currentUser.displayName,
        history: [
          ...(request?.history || []),
          {
            action: "Estimated cost updated to",
            at: new Date().toLocaleString(),
            by: currentUser.displayName,
            to: `₱${estimatedCost}`,
          },
        ],
      };

      await setDocData("serviceRequests", request.id, updatedRequest);
      setSelectedRequest(updatedRequest);
      toast.success("Estimated cost updated successfully");
    } catch (error) {
      console.error("Error updating estimated cost:", error);
      toast.error("Failed to update estimated cost");
    } finally {
      setCostSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses =
      "px-2.5 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1";

    switch (status) {
      case "Pending":
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            <FiClock className="w-3 h-3" />
            <span>Pending</span>
          </span>
        );
      case "Scheduled":
        return (
          <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
            <FiCalendar className="w-3 h-3" />
            <span>Scheduled</span>
          </span>
        );
      case "In Progress":
        return (
          <span className={`${baseClasses} bg-purple-100 text-purple-800`}>
            <FiTool className="w-3 h-3" />
            <span>In Progress</span>
          </span>
        );
      case "Completed":
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            <FiCheckCircle className="w-3 h-3" />
            <span>Completed</span>
          </span>
        );
      case "Cancelled":
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            <FiX className="w-3 h-3" />
            <span>Cancelled</span>
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {status}
          </span>
        );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <>
      <div className="p-6 max-w-5xl mx-auto bg-white rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-[#363062]">
            Service Request Details
          </h2>
          <div className="flex items-center gap-3">
            {canEdit && (
              <button
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  editMode
                    ? "bg-red-500 text-white hover:bg-red-600"
                    : "bg-[#363062] text-white hover:bg-opacity-90"
                }`}
                onClick={() => setEditMode(!editMode)}
              >
                {editMode ? (
                  <>
                    <FiX className="w-4 h-4" />
                    Cancel Edit
                  </>
                ) : (
                  <>
                    <FiEdit2 className="w-4 h-4" />
                    Edit Request
                  </>
                )}
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Status Indicator and Update */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
          <div>{getStatusBadge(request.status)}</div>
          {editMode && (
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <SelectInput
                options={statusOptions}
                onChange={(selected) => handleSelectStatus(selected)}
                value={statusOptions.find(
                  (option) => option.value === newStatus
                )}
                className="w-full md:w-48"
              />
              <button
                className="px-4 py-2 bg-[#363062] text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm flex items-center gap-2 text-sm"
                disabled={!updateHappened || statusUpdateLoading}
                onClick={handleUpdateStatus}
              >
                {statusUpdateLoading ? (
                  <>
                    <IOSSpinner className="w-4 h-4" />
                    <span>Updating...</span>
                  </>
                ) : (
                  "Update Status"
                )}
              </button>
            </div>
          )}
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Details */}
          <div className="md:col-span-2 space-y-6">
            {/* Request Information */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <h3 className="text-sm font-semibold text-[#363062] mb-4">
                Request Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <h4 className="text-xs text-gray-500 mb-1">Request ID</h4>
                  <p className="text-sm">{request.id}</p>
                </div>
                <div>
                  <h4 className="text-xs text-gray-500 mb-1">
                    <CompanyTerms term="identifier" capitalize={true} />
                  </h4>
                  <p className="text-sm">{request.identifier}</p>
                </div>
                <div>
                  <h4 className="text-xs text-gray-500 mb-1">Requested By</h4>
                  <p className="text-sm">{request.userName || "N/A"}</p>
                  <p className="text-xs text-gray-500">
                    {request.userEmail || "N/A"}
                  </p>
                </div>
                <div>
                  <h4 className="text-xs text-gray-500 mb-1">Service Type</h4>
                  <p className="text-sm">{request.serviceType}</p>
                </div>
                <div>
                  <h4 className="text-xs text-gray-500 mb-1">Preferred Date</h4>
                  <p className="text-sm">{formatDate(request.preferredDate)}</p>
                </div>
                <div>
                  <h4 className="text-xs text-gray-500 mb-1">Preferred Time</h4>
                  <p className="text-sm">{request.preferredTime || "N/A"}</p>
                </div>
              </div>

              <div>
                <h4 className="text-xs text-gray-500 mb-1">Description</h4>
                <div className="p-3 bg-gray-50 rounded-lg text-sm">
                  {request.description}
                </div>
              </div>
            </div>

            {/* Image Section */}
            {request.image && (
              <div>
                <h3 className="text-sm font-semibold text-[#363062] mb-2">
                  Attached Image
                </h3>
                <div className="w-full overflow-hidden rounded-lg border border-gray-200">
                  <PhotoCarousel images={[request.image]} />
                </div>
              </div>
            )}

            {/* Comments Section */}
            <CommentsSection
              comments={comments}
              onCommentSubmit={handleCommentSubmit}
              onCommentDelete={handleCommentDelete}
              currentUser={currentUser}
              isSubmitting={savingComment}
            />
          </div>

          {/* Right Column - Assignment & Resolution */}
          <div className="space-y-6">
            {/* Assignment Section */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <h3 className="text-sm font-semibold text-[#363062] mb-4">
                Task Assignment
              </h3>

              <TaskAssignmentModule
                assignedToArray={assignedToArray}
                setAssignedToArray={setAssignedToArray}
                editMode={editMode}
              />

              {editMode && (
                <div className="mt-4 flex justify-end">
                  <button
                    className="px-4 py-2 bg-[#363062] text-white rounded-lg hover:bg-opacity-90 font-medium shadow-sm flex items-center gap-2 text-xs disabled:opacity-70"
                    onClick={handleAssignmentUpdate}
                    disabled={assignmentSaving}
                  >
                    {assignmentSaving ? (
                      <>
                        <IOSSpinner className="w-4 h-4" />
                        <span>Updating...</span>
                      </>
                    ) : (
                      "Update Assignment"
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Estimated Cost Section */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <h3 className="text-sm font-semibold text-[#363062] mb-4 flex items-center gap-2">
                Estimated Cost (PHP)
              </h3>

              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500">₱</span>
                </div>
                <input
                  type="text"
                  className="w-full pl-7 pr-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#363062] focus:border-transparent text-xs"
                  placeholder="Enter estimated cost"
                  value={estimatedCost}
                  onChange={handleCostChange}
                  disabled={!editMode}
                />
              </div>

              {editMode && (
                <div className="mt-4 flex justify-end">
                  <button
                    className="px-4 py-2 bg-[#363062] text-white rounded-lg hover:bg-opacity-90 font-medium shadow-sm flex items-center gap-2 text-xs"
                    onClick={handleCostUpdate}
                    disabled={costSaving}
                  >
                    {costSaving ? (
                      <>
                        <IOSSpinner className="w-4 h-4" />
                        <span>Updating...</span>
                      </>
                    ) : (
                      "Update Cost"
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Resolution Section */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <h3 className="text-sm font-semibold text-[#363062] mb-4">
                Resolution Report
              </h3>

              <textarea
                name="resolution"
                className="w-full rounded-lg p-3 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#363062] focus:border-transparent resize-none text-xs"
                rows="6"
                onChange={handleResolutionChange}
                placeholder="Add resolution report here..."
                value={resolutionText}
                disabled={!editMode}
              ></textarea>

              {editMode && (
                <div className="mt-4 flex justify-end">
                  <button
                    className="px-4 py-2 bg-[#363062] text-white rounded-lg hover:bg-opacity-90 font-medium shadow-sm flex items-center gap-2 text-xs"
                    onClick={handleResolutionUpdate}
                    disabled={resolutionSaving}
                  >
                    {resolutionSaving ? (
                      <>
                        <IOSSpinner className="w-4 h-4" />
                        <span>Updating...</span>
                      </>
                    ) : (
                      "Update Resolution"
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* History Section */}
            <HistoryModule data={request.history || []} />
          </div>
        </div>
      </div>
    </>
  );
}
